import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle } from 'lucide-react';
import api from '../utils/api';

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const errorParam = params.get('error');

        if (errorParam) {
          setError(`Google login failed: ${errorParam}. ${errorParam === 'redirect_uri_mismatch' ? 'The app redirect URI needs to be added to Google Cloud Console.' : ''}`);
          return;
        }

        if (!code) {
          setError('No authorization code received from Google.');
          return;
        }

        const redirectUri = (process.env.REACT_APP_APP_URL || window.location.origin) + '/auth/callback';
        const response = await api.post('/auth/google/callback', {
          code,
          redirect_uri: redirectUri
        });

        if (response.data.success) {
          const userData = response.data.user;
          const token = response.data.token;
          if (token) localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('isAuthenticated', 'true');
          setUser(userData);
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          setError('Authentication failed. Please try again.');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        if (detail.includes('redirect_uri_mismatch') || detail.includes('redirect')) {
          setError(`Redirect URI mismatch. Add this URL to your Google OAuth app:\n\n${process.env.REACT_APP_APP_URL || window.location.origin}/auth/callback`);
        } else {
          setError(`Google authentication failed: ${detail}`);
        }
      }
    };

    processGoogleAuth();
  }, [location.search, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-lg mx-auto px-6" data-testid="oauth-error">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-3">Login Failed</h2>
          <p className="text-muted-foreground text-sm whitespace-pre-wrap mb-6">{error}</p>
          <div className="p-3 bg-muted rounded-lg border border-input mb-4">
            <p className="text-xs text-muted-foreground mb-1">Required Redirect URI:</p>
            <code className="text-xs text-blue-400 break-all">{process.env.REACT_APP_APP_URL || window.location.origin}/auth/callback</code>
          </div>
          <button onClick={() => navigate('/', { replace: true })} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-all" data-testid="oauth-error-back-btn">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Signing you in...</h2>
        <p className="text-muted-foreground">Completing your Google authentication</p>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
